/**
 * Look things up instead of looking for them.
 *
 * The app keeps the family's records as plain lists — every story, every
 * picture, every recording — and almost every question a screen asks is of the
 * form "which of these belong to *that*?": the pictures inside this wedding,
 * the stories this person told, the recordings on this story. Asked with
 * `filter` and `find`, each of those walks the whole list; asked once per row of
 * another list — per person in the tree, per line on the timeline — it is the
 * whole list *times* the other one. Forty pictures and thirty people never
 * noticed. Fifty thousand pictures and a thousand people is fifty million steps
 * to draw one screen, on a phone, every time anybody in the family does
 * anything.
 *
 * So a list is sorted into piles once, and the piles are kept for as long as
 * the list is the same list. "The same list" is its identity: AppState hands
 * out a new array whenever the data is reloaded, so an index made from the old
 * one is simply never asked for again and goes with it (the cache is a
 * WeakMap, which is what lets it go). The length is remembered too, so a list
 * that was added to in place — the mock's own tables are — is sorted afresh
 * rather than answered from a stale pile.
 *
 * Two things follow for whoever calls these:
 *
 *   - **pass the same function every time** (a module-level constant, never an
 *     arrow written at the call site): the piles are remembered per function;
 *   - **only sort by something that does not change while the list lives.** For
 *     the lists AppState hands out that is anything at all, because they are
 *     replaced on every change. For the mock's own long-lived tables, whose rows
 *     are edited in place, it means ids and nothing else.
 */

type Piles = Map<unknown, unknown[]>;
interface Kept {
  length: number;
  piles: Map<unknown, Piles>;
  ids?: Map<string, unknown>;
}

const kept = new WeakMap<readonly unknown[], Kept>();

function keptFor(rows: readonly unknown[]): Kept {
  let k = kept.get(rows);
  if (!k || k.length !== rows.length) {
    k = { length: rows.length, piles: new Map() };
    kept.set(rows, k);
  }
  return k;
}

const NONE: readonly never[] = Object.freeze([]);

/**
 * Every row under the key it has. A row whose key is null or undefined is left
 * out. The piles keep the list's own order, so a list that arrives newest-first
 * gives piles that are newest-first.
 */
export function groupedBy<T, K>(rows: readonly T[], keyOf: (row: T) => K | null | undefined): ReadonlyMap<K, readonly T[]> {
  const k = keptFor(rows);
  let piles = k.piles.get(keyOf);
  if (!piles) {
    piles = new Map();
    for (const row of rows) {
      const key = keyOf(row);
      if (key === null || key === undefined) continue;
      const pile = piles.get(key);
      if (pile) pile.push(row);
      else piles.set(key, [row]);
    }
    k.piles.set(keyOf, piles);
  }
  return piles as Map<K, T[]>;
}

/** The same, for a row that belongs under several keys at once — a picture with four people pinned to it. */
export function groupedByEach<T, K>(rows: readonly T[], keysOf: (row: T) => readonly K[] | null | undefined): ReadonlyMap<K, readonly T[]> {
  const k = keptFor(rows);
  let piles = k.piles.get(keysOf);
  if (!piles) {
    piles = new Map();
    for (const row of rows) {
      const keys = keysOf(row);
      if (!keys) continue;
      for (const key of keys) {
        const pile = piles.get(key);
        // The same person pinned twice must not put the picture on their circle twice.
        if (pile) {
          if (pile[pile.length - 1] !== row) pile.push(row);
        } else piles.set(key, [row]);
      }
    }
    k.piles.set(keysOf, piles);
  }
  return piles as Map<K, T[]>;
}

/** One pile, never undefined — so a caller can loop over it without asking first. */
export function pileOf<T, K>(piles: ReadonlyMap<K, readonly T[]>, key: K): readonly T[] {
  return piles.get(key) ?? NONE;
}

/** The row with this id, without walking the list to find it. */
export function byId<T extends { id: string }>(rows: readonly T[]): ReadonlyMap<string, T> {
  const k = keptFor(rows);
  if (!k.ids) {
    const ids = new Map<string, unknown>();
    for (const row of rows) ids.set(row.id, row);
    k.ids = ids;
  }
  return k.ids as Map<string, T>;
}
